import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiCheckCircle, mdiDownload, mdiLinkOff } from '@mdi/js'
import type { BallotReceipt, BallotState } from '../../api/schema'
import { BallotApiError, ballotApi } from '../../hooks/useBallots'
import { useFormatters } from '../../lib/format'
import { answerMfaWithPasskey, browserSupportsWebAuthn } from '../../lib/passkeys'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { tokenFromHash } from '../signatures/signature-utils'
import { LiveSection } from './LiveSection'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { AppIcon } from '../ui/icon'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Separator } from '../ui/separator'

const sessionKey = (orgCode: string) => `ballot-session:${orgCode}`
const VOTES = ['for', 'against', 'abstain'] as const

type Phase = 'loading' | 'ready' | 'not_found' | 'expired' | 'unavailable'

function useNoReferrer() {
    useEffect(() => {
        const meta = document.createElement('meta')
        meta.name = 'referrer'
        meta.content = 'no-referrer'
        document.head.appendChild(meta)
        return () => {
            document.head.removeChild(meta)
        }
    }, [])
}

// The public page speaks the voter's language and carries its own title, not
// the internal tool's generic one.
function usePublicDocument(title: string, lang: string) {
    useEffect(() => {
        const previousTitle = document.title
        const previousLang = document.documentElement.lang
        document.title = title
        document.documentElement.lang = lang
        return () => {
            document.title = previousTitle
            document.documentElement.lang = previousLang
        }
    }, [title, lang])
}

/**
 * The co-owner's remote ballot (spec 2026-10-05-08 §6), reached from the link
 * of the convocation. It lives outside routes/$org: the voter has no account
 * here. The link carries its token in the fragment (#t=...), exchanged once
 * for a short session kept in this tab's sessionStorage and removed from the
 * address bar at once. The token alone only opens the identification; then
 * the voter declares how they take part, fills the mail-vote form or gives
 * their proxy.
 */
export function BallotPage({ orgCode }: { orgCode: string }) {
    const { t, i18n } = useTranslation('ballots')
    useNoReferrer()
    usePublicDocument(t('page_title'), i18n.language?.slice(0, 2) || 'fr')
    const [phase, setPhase] = useState<Phase>('loading')
    const [session, setSession] = useState<string | null>(null)
    const [state, setState] = useState<BallotState | null>(null)
    const started = useRef(false)

    const adopt = useCallback(
        (s: string, st: BallotState) => {
            sessionStorage.setItem(sessionKey(orgCode), s)
            setSession(s)
            setState(st)
            setPhase('ready')
        },
        [orgCode],
    )

    const onError = useCallback((e: unknown) => {
        if (e instanceof BallotApiError && e.status === 401) {
            setPhase('expired')
            return true
        }
        if (e instanceof BallotApiError && e.status === 404 && e.code === 'NOT_FOUND') {
            setPhase('not_found')
            return true
        }
        return false
    }, [])

    // A 4xx says the link is not (or no longer) one; a 5xx or a network error
    // says the server could not answer, which a new try may fix.
    const fail = useCallback(
        (e: unknown) => {
            if (onError(e)) return
            setPhase(e instanceof BallotApiError && e.status < 500 ? 'not_found' : 'unavailable')
        },
        [onError],
    )

    // Opens the ballot from the token of the address bar (removed at once, so it
    // never stays in the history), else from the session of this tab.
    const load = useCallback(() => {
        const token = tokenFromHash(window.location.hash)
        if (token) {
            window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
            setPhase('loading')
            ballotApi
                .openSession(orgCode, token)
                .then((res) => adopt(res.session, res.state))
                .catch(fail)
            return
        }
        const stored = sessionStorage.getItem(sessionKey(orgCode))
        if (!stored) {
            setPhase('not_found')
            return
        }
        setPhase('loading')
        ballotApi
            .current(orgCode, stored)
            .then((st) => adopt(stored, st))
            .catch(fail)
    }, [orgCode, adopt, fail])

    useEffect(() => {
        if (started.current) return
        started.current = true
        load()
    }, [load])

    // A new link opened in the same tab only changes the fragment.
    useEffect(() => {
        const onHash = () => {
            if (tokenFromHash(window.location.hash)) load()
        }
        window.addEventListener('hashchange', onHash)
        return () => window.removeEventListener('hashchange', onHash)
    }, [load])

    if (phase === 'loading') {
        return <div className="mx-auto max-w-3xl p-6 text-muted-foreground" data-testid="ballot-loading">{t('loading')}</div>
    }
    if (phase === 'unavailable') {
        return (
            <div className="mx-auto max-w-xl space-y-3 p-6 text-center" data-testid="ballot-unavailable">
                <AppIcon path={mdiLinkOff} size="lg" className="mx-auto text-muted-foreground" />
                <p className="text-lg font-semibold text-foreground">{t('unavailable_title')}</p>
                <p className="text-sm text-muted-foreground">{t('unavailable_body')}</p>
                <Button variant="outline" onClick={load} data-testid="ballot-retry">{t('retry')}</Button>
            </div>
        )
    }
    if (phase !== 'ready' || !state || !session) {
        return (
            <div className="mx-auto max-w-xl space-y-3 p-6 text-center" data-testid={phase === 'expired' ? 'ballot-expired' : 'ballot-not-found'}>
                <AppIcon path={mdiLinkOff} size="lg" className="mx-auto text-muted-foreground" />
                <p className="text-lg font-semibold text-foreground">{phase === 'expired' ? t('expired_title') : t('not_found_title')}</p>
                <p className="text-sm text-muted-foreground">{phase === 'expired' ? t('expired_body') : t('not_found_body')}</p>
            </div>
        )
    }
    return <BallotFlow orgCode={orgCode} session={session} state={state} onSession={adopt} onState={setState} onError={onError} />
}

interface FlowProps {
    orgCode: string
    session: string
    state: BallotState
    onSession: (s: string, st: BallotState) => void
    onState: (st: BallotState) => void
    onError: (e: unknown) => boolean
}

function BallotFlow({ orgCode, session, state, onSession, onState, onError }: FlowProps) {
    const { t } = useTranslation('ballots')
    const { formatDate } = useFormatters()
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const [receipt, setReceipt] = useState<BallotReceipt | null>(null)
    const [warnings, setWarnings] = useState<string[]>([])

    const run = async (fn: () => Promise<void>) => {
        setBusy(true)
        setError(null)
        try {
            await fn()
        } catch (e) {
            if (!onError(e)) setError(e instanceof Error ? e.message : String(e))
        } finally {
            setBusy(false)
        }
    }
    const day = (d?: string) => (d ? formatDate(d, { dateStyle: 'long' }) : '—')
    const voter = state.voter

    return (
        <div className="mx-auto w-full min-w-0 max-w-3xl space-y-6 p-4 pb-16 md:p-6" data-testid="ballot-page">
            <PageHeader
                wrap
                icon={SECTION_ICONS.assemblies}
                title={<span data-testid="ballot-title">{state.assembly_name}</span>}
                description={`${state.organization_name} · ${t('held_on', { date: day(state.held_on) })}${state.held_location ? ` · ${state.held_location}` : ''}`}
            />
            <p className="text-sm text-muted-foreground" data-testid="ballot-deadline">
                {state.open ? t('deadline', { date: day(state.remote_vote_last_day) }) : t('closed')}
            </p>

            {state.status === 'suspended' && (
                <Alert variant="warning" data-testid="ballot-suspended">
                    <AlertTitle>{t('suspended_title')}</AlertTitle>
                    <AlertDescription>{t('suspended_body')}</AlertDescription>
                </Alert>
            )}

            {!state.verified && state.status !== 'suspended' && (
                <Identification
                    state={state}
                    busy={busy}
                    onSend={() => run(async () => {
                        const res = await ballotApi.challenge(orgCode, session)
                        onSession(res.session, res.state)
                    })}
                    onVerify={(code) => run(async () => {
                        const res = await ballotApi.verify(orgCode, session, code)
                        onSession(res.session, res.state)
                    })}
                    onPasskey={() => run(async () => {
                        if (!browserSupportsWebAuthn()) throw new Error(t('passkey_unsupported'))
                        const answer = await answerMfaWithPasskey(() => ballotApi.beginPasskey(orgCode, session))
                        const res = await ballotApi.verifyPasskey(orgCode, session, answer.session, answer.credential)
                        onSession(res.session, res.state)
                    })}
                />
            )}

            {state.verified && <LiveSection orgCode={orgCode} session={session} onError={onError} />}

            {voter && (
                <>
                    <p className="text-sm" data-testid="ballot-voter">{t('voter', { name: voter.name, voices: voter.voices })}</p>
                    {receipt && (
                        <Alert data-testid="ballot-receipt">
                            <AppIcon path={mdiCheckCircle} size="sm" />
                            <AlertTitle>{receipt.unchanged ? t('receipt_unchanged') : t('receipt_title')}</AlertTitle>
                            <AlertDescription>
                                {t('receipt_body', {
                                    date: formatDate(receipt.received_at!, { dateStyle: 'short', timeStyle: 'medium' }),
                                    reference: receipt.reference,
                                })}
                            </AlertDescription>
                        </Alert>
                    )}
                    {(voter.forms_count ?? 0) > 0 && (
                        <Button
                            variant="outline"
                            data-testid="ballot-download"
                            onClick={() => run(async () => {
                                const buf = await ballotApi.formPdf(orgCode, session)
                                const url = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }))
                                const a = document.createElement('a')
                                a.href = url
                                a.download = 'formulaire-vote.pdf'
                                a.click()
                                URL.revokeObjectURL(url)
                            })}
                        >
                            <AppIcon path={mdiDownload} size="sm" className="mr-2" />
                            {t('download')}
                        </Button>
                    )}
                    {state.status !== 'suspended' && state.open !== undefined && (
                        <ModeStep
                            state={state}
                            busy={busy}
                            onMode={(mode) => run(async () => onState(await ballotApi.mode(orgCode, session, mode)))}
                        />
                    )}
                    {voter.proxy ? (
                        <Alert data-testid="ballot-proxy-given">
                            <AlertTitle>{t('proxy_given_title')}</AlertTitle>
                            <AlertDescription>
                                {voter.proxy.blank ? t('proxy_blank') : t('proxy_to', {
                                    name: voter.proxy.holder_name ||
                                        voter.co_owners?.find((c) => c.stakeholder_uid === voter.proxy?.holder_stakeholder_uid)?.name || '—',
                                })}
                                {warnings.length > 0 && (
                                    <ul className="mt-2 list-disc pl-5" data-testid="ballot-proxy-warnings">
                                        {warnings.map((w) => <li key={w}>{t(`warning.${w}`)}</li>)}
                                    </ul>
                                )}
                            </AlertDescription>
                        </Alert>
                    ) : (
                        state.open && state.status !== 'suspended' && (
                            <FormStep
                                state={state}
                                busy={busy}
                                onSubmit={(answers, place) => run(async () => {
                                    setReceipt(await ballotApi.submitForm(orgCode, session, answers, place))
                                    onState(await ballotApi.current(orgCode, session))
                                })}
                            />
                        )
                    )}
                    {!voter.proxy && state.status !== 'suspended' && (
                        <ProxyStep
                            state={state}
                            busy={busy}
                            warnings={warnings}
                            onGive={(body) => run(async () => {
                                const res = await ballotApi.proxy(orgCode, session, body)
                                setWarnings(res.warnings)
                                onState(res.state)
                            })}
                        />
                    )}
                </>
            )}

            {error && (
                <Alert variant="destructive" data-testid="ballot-error">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}
        </div>
    )
}

function Identification({ state, busy, onSend, onVerify, onPasskey }: {
    state: BallotState
    busy: boolean
    onSend: () => void
    onVerify: (code: string) => void
    onPasskey: () => void
}) {
    const { t } = useTranslation('ballots')
    const [code, setCode] = useState('')
    const strong = state.identity_level === 'strong'
    if (state.locked_until) {
        return (
            <Alert variant="warning" data-testid="ballot-locked">
                <AlertTitle>{t('locked_title')}</AlertTitle>
                <AlertDescription>{t('locked_body')}</AlertDescription>
            </Alert>
        )
    }
    if (!strong && !state.code_channel) {
        return (
            <Alert variant="warning" data-testid="ballot-no-channel">
                <AlertTitle>{t('no_channel_title')}</AlertTitle>
                <AlertDescription>{t('no_channel_body')}</AlertDescription>
            </Alert>
        )
    }
    return (
        <section className="space-y-3" data-testid="ballot-identification">
            <h2 className="text-base font-semibold text-foreground">{t('identify_title')}</h2>
            <p className="text-sm text-muted-foreground">
                {strong ? t('identify_strong') : t('identify_otp', { destination: state.code_destination, channel: t(`channel.${state.code_channel}`) })}
            </p>
            {!strong && (
                <Button variant="outline" disabled={busy} onClick={onSend} data-testid="ballot-send-code">
                    {state.code_sent ? t('resend_code') : t('send_code')}
                </Button>
            )}
            {(strong || state.code_sent) && (
                <form
                    className="flex flex-wrap items-end gap-2"
                    onSubmit={(e) => {
                        e.preventDefault()
                        onVerify(code)
                    }}
                >
                    <div className="space-y-1">
                        <Label htmlFor="ballot-code">{strong ? t('totp_label') : t('code_label')}</Label>
                        <Input
                            id="ballot-code"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            className="w-40"
                            data-testid="ballot-code-input"
                        />
                    </div>
                    <Button type="submit" disabled={busy || code.trim().length < 6} data-testid="ballot-verify">{t('verify')}</Button>
                </form>
            )}
            {strong && state.strong_factors?.includes('passkey') && (
                <Button variant="outline" disabled={busy} onClick={onPasskey} data-testid="ballot-passkey">{t('passkey')}</Button>
            )}
        </section>
    )
}

function ModeStep({ state, busy, onMode }: { state: BallotState; busy: boolean; onMode: (mode: string) => void }) {
    const { t } = useTranslation('ballots')
    const voter = state.voter!
    return (
        <section className="space-y-2" data-testid="ballot-mode">
            <Separator />
            <h2 className="text-base font-semibold text-foreground">{t('mode_title')}</h2>
            <div className="flex flex-wrap gap-2">
                {(voter.participation_modes ?? []).map((m) => (
                    <Button
                        key={m}
                        size="sm"
                        variant={voter.participation_mode === m ? 'default' : 'outline'}
                        disabled={busy}
                        onClick={() => onMode(m)}
                        data-testid={`ballot-mode-${m}`}
                    >
                        {t(`mode.${m}`)}
                    </Button>
                ))}
            </div>
            {!voter.participation_modes?.includes('video') && <p className="text-xs text-muted-foreground">{t('no_video')}</p>}
        </section>
    )
}

function FormStep({ state, busy, onSubmit }: { state: BallotState; busy: boolean; onSubmit: (answers: Record<string, string>, place: string) => void }) {
    const { t } = useTranslation('ballots')
    const resolutions = state.voter?.resolutions ?? []
    const [answers, setAnswers] = useState<Record<string, string>>({})
    const [place, setPlace] = useState('')
    const [consent, setConsent] = useState(false)
    const complete = resolutions.every((r) => !!answers[r.uid!])
    return (
        <section className="space-y-4" data-testid="ballot-form">
            <Separator />
            <div>
                <h2 className="text-base font-semibold text-foreground">{t('form_title')}</h2>
                <p className="text-sm text-muted-foreground">{t('form_help')}</p>
            </div>
            {resolutions.map((r) => (
                <fieldset key={r.uid} className="space-y-2 rounded-md border p-3" data-testid={`ballot-resolution-${r.uid}`}>
                    <legend className="px-1 text-sm font-semibold">{t('resolution', { number: r.number, title: r.title })}</legend>
                    {r.text && <p className="whitespace-pre-line text-sm text-foreground">{r.text}</p>}
                    <div className="flex flex-wrap gap-4" role="radiogroup" aria-label={r.title}>
                        {VOTES.map((v) => (
                            <label key={v} className="flex items-center gap-2 text-sm">
                                <input
                                    type="radio"
                                    name={`vote-${r.uid}`}
                                    value={v}
                                    checked={answers[r.uid!] === v}
                                    onChange={() => setAnswers((a) => ({ ...a, [r.uid!]: v }))}
                                    data-testid={`ballot-answer-${r.uid}-${v}`}
                                />
                                {t(`vote.${v}`)}
                            </label>
                        ))}
                    </div>
                    <p className="break-all text-xs text-muted-foreground">{t('text_sha', { sha: r.text_sha256 })}</p>
                </fieldset>
            ))}
            <div className="space-y-1">
                <Label htmlFor="ballot-place">{t('place')}</Label>
                <Input id="ballot-place" value={place} onChange={(e) => setPlace(e.target.value)} className="max-w-xs" data-testid="ballot-place" />
            </div>
            <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={consent} onCheckedChange={(v) => setConsent(v === true)} data-testid="ballot-consent" />
                <span>{t('consent')}</span>
            </label>
            {!complete && <p className="text-xs text-muted-foreground" data-testid="ballot-incomplete">{t('incomplete')}</p>}
            <Button disabled={busy || !complete || !consent} onClick={() => onSubmit(answers, place)} data-testid="ballot-submit">
                {t('submit')}
            </Button>
        </section>
    )
}

function ProxyStep({ state, busy, warnings, onGive }: {
    state: BallotState
    busy: boolean
    warnings: string[]
    onGive: (body: { holder_stakeholder_uid?: string; holder_name?: string }) => void
}) {
    const { t } = useTranslation('ballots')
    const [holder, setHolder] = useState('')
    const [name, setName] = useState('')
    const [consent, setConsent] = useState(false)
    return (
        <section className="space-y-3" data-testid="ballot-proxy">
            <Separator />
            <div>
                <h2 className="text-base font-semibold text-foreground">{t('proxy_title')}</h2>
                <p className="text-sm text-muted-foreground">{t('proxy_help')}</p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                    <Label htmlFor="ballot-proxy-holder">{t('proxy_holder')}</Label>
                    <select
                        id="ballot-proxy-holder"
                        value={holder}
                        onChange={(e) => setHolder(e.target.value)}
                        className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
                        data-testid="ballot-proxy-holder"
                    >
                        <option value="">{t('proxy_other')}</option>
                        {(state.voter?.co_owners ?? []).map((c) => (
                            <option key={c.stakeholder_uid} value={c.stakeholder_uid}>{c.name}</option>
                        ))}
                    </select>
                </div>
                {!holder && (
                    <div className="space-y-1">
                        <Label htmlFor="ballot-proxy-name">{t('proxy_name')}</Label>
                        <Input id="ballot-proxy-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="ballot-proxy-name" />
                    </div>
                )}
            </div>
            <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={consent} onCheckedChange={(v) => setConsent(v === true)} data-testid="ballot-proxy-consent" />
                <span>{t('proxy_consent')}</span>
            </label>
            {warnings.length > 0 && (
                <ul className="list-disc pl-5 text-sm text-warning-foreground" data-testid="ballot-proxy-warnings">
                    {warnings.map((w) => <li key={w}>{t(`warning.${w}`)}</li>)}
                </ul>
            )}
            <Button
                variant="outline"
                disabled={busy || !consent}
                onClick={() => onGive(holder ? { holder_stakeholder_uid: holder } : name.trim() ? { holder_name: name.trim() } : {})}
                data-testid="ballot-proxy-give"
            >
                {t('proxy_give')}
            </Button>
        </section>
    )
}
